import { useEffect, useMemo, useRef, useState } from 'react'
import type { GraphApiResponse, GraphPeriod } from './types'
import { fetchGraphData } from './api'

export type GraphApiState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | { status: 'success'; data: GraphApiResponse }

type Options = {
    apiUrl: string
    headers?: Record<string, string>
    period?: GraphPeriod
    pollIntervalMs?: number
    enabled?: boolean
}

function stableHeadersKey(headers?: Record<string, string>): string {
    if (!headers) return ''
    const entries = Object.entries(headers)
    if (entries.length === 0) return ''
    entries.sort(([a], [b]) => a.localeCompare(b))
    return JSON.stringify(entries)
}

export function useGraphApiResponse(options: Options): GraphApiState {
    const { apiUrl, headers, period, pollIntervalMs, enabled = true } = options

    const [state, setState] = useState<GraphApiState>({ status: 'idle' })
    const abortRef = useRef<AbortController | null>(null)

    const headersKey = useMemo(() => stableHeadersKey(headers), [headers])

    useEffect(() => {
        if (!enabled) return

        let disposed = false

        async function load(): Promise<void> {
            abortRef.current?.abort()
            const controller = new AbortController()
            abortRef.current = controller

            setState({ status: 'loading' })
            try {
                const data = await fetchGraphData({ apiUrl, headers, period, signal: controller.signal })
                if (disposed) return
                setState({ status: 'success', data })
            } catch (error) {
                if (disposed) return
                if (error instanceof DOMException && error.name === 'AbortError') return
                const message = error instanceof Error ? error.message : String(error)
                setState({ status: 'error', message })
            }
        }

        void load()

        let timer: number | undefined

        function startTimer(): void {
            stopTimer()
            if (pollIntervalMs && pollIntervalMs > 0) {
                timer = window.setInterval(() => {
                    void load()
                }, pollIntervalMs)
            }
        }

        function stopTimer(): void {
            if (timer) {
                window.clearInterval(timer)
                timer = undefined
            }
        }

        startTimer()

        // Pause polling while the tab is hidden to avoid unnecessary requests.
        function handleVisibilityChange(): void {
            if (document.hidden) {
                stopTimer()
            } else {
                // Fetch immediately when the user returns, then resume polling.
                void load()
                startTimer()
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            disposed = true
            abortRef.current?.abort()
            stopTimer()
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
        // headersKey is the stable representation for headers.
    }, [apiUrl, headersKey, period, pollIntervalMs, enabled])

    return state
}
